import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isAudioSupported, playSmartAudio } from '../utils/audioUtils';

interface WordOfTheDay {
  id: string;
  persian: string;
  english: string;
  transliteration: string;
  category: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  audioUrl?: string;
}

interface RecentActivity {
  id: string;
  type: 'word_learned' | 'quiz_completed' | 'streak_milestone' | 'lesson_completed' | 'welcome';
  title: string;
  description: string;
  timestamp: Date;
  icon: string;
}

interface DashboardData {
  user: {
    id: string;
    firstName?: string;
    username: string;
    stats: {
      totalWordsLearned: number;
      currentStreak: number;
      longestStreak: number;
      level: number;
      xpPoints: number;
      totalStudyTime: number;
    };
    preferences: any;
  };
  wordOfTheDay: WordOfTheDay | null;
  recentActivity: RecentActivity[];
  progressStats: {
    totalWordsLearned: number;
    totalWordsInSystem: number;
    currentStreak: number;
    longestStreak: number;
    level: number;
    xpPoints: number;
    totalStudyTime: number;
  };
  recentProgress: Array<{
    date: string;
    wordsLearned: number;
    timeSpent: number;
    accuracy: number;
  }>;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/api/dashboard', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }

      const result = await response.json();
      
      if (result.success) {
        // Convert timestamp strings to Date objects for recent activity
        const processedData = {
          ...result.data,
          recentActivity: result.data.recentActivity.map((activity: any) => ({
            ...activity,
            timestamp: new Date(activity.timestamp)
          }))
        };
        
        setDashboardData(processedData);
      } else {
        throw new Error(result.message || 'Failed to fetch dashboard data');
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    {
      title: 'Word of the Day',
      description: 'Learn today\'s new word',
      icon: '📅',
      color: 'from-blue-500 to-blue-600',
      link: '/word-of-the-day'
    },
    {
      title: 'Flashcards',
      description: 'Practice with smart flashcards',
      icon: '🃏',
      color: 'from-green-500 to-green-600',
      link: '/flashcards'
    },
    {
      title: 'Quiz',
      description: 'Take a quiz and measure your progress',
      icon: '❓',
      color: 'from-purple-500 to-purple-600',
      link: '/quiz'
    },
    {
      title: 'Progress',
      description: 'Charts and progress statistics',
      icon: '📈',
      color: 'from-red-500 to-red-600',
      link: '/progress'
    }
  ];

  const progressStats = [
    {
      label: 'Words Learned',
      value: dashboardData?.progressStats.totalWordsLearned || 0,
      total: dashboardData?.progressStats.totalWordsInSystem || 1000,
      color: 'text-green-600'
    },
    {
      label: 'Current Streak',
      value: dashboardData?.progressStats.currentStreak || 0,
      total: 365,
      color: 'text-orange-600'
    },
    {
      label: 'Current Level',
      value: dashboardData?.progressStats.level || 1,
      total: 10,
      color: 'text-blue-600'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Loading Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={fetchDashboardData}
            className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="text-gray-500 text-xl mb-4">📊</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Data Found</h2>
          <p className="text-gray-600">Dashboard information is not available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">
              Hello {dashboardData?.user.firstName || dashboardData?.user.username || user?.firstName || user?.username}! 👋
            </h1>
            <p className="text-orange-100 text-lg">
              Ready to learn new Persian words?
            </p>
          </div>
          <div className="hidden md:block">
            <div className="text-right">
              <div className="text-2xl font-bold">{dashboardData?.progressStats.currentStreak || 0}</div>
              <div className="text-orange-100">Day Streak</div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {progressStats.map((stat, index) => (
          <div key={index} className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {stat.label}
              </h3>
              <span className={`text-2xl font-bold ${stat.color}`}>
                {stat.value}
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${
                  stat.color === 'text-green-600' ? 'bg-green-500' :
                  stat.color === 'text-orange-600' ? 'bg-orange-500' : 'bg-blue-500'
                }`}
                style={{ width: `${Math.min((stat.value / stat.total) * 100, 100)}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              of {stat.total} {stat.label === 'Current Level' ? 'levels' : 'words'}
            </p>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {quickActions.map((action, index) => (
            <Link
              key={index}
              to={action.link}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
            >
              <div className={`w-12 h-12 bg-gradient-to-r ${action.color} rounded-lg flex items-center justify-center text-2xl mb-4`}>
                {action.icon}
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {action.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                {action.description}
              </p>
            </Link>
          ))}
        </div>
      </div>

      {/* Word of the Day */}
      {dashboardData?.wordOfTheDay && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Word of the Day 📅
            </h2>
            <span className="px-3 py-1 bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200 rounded-full text-sm font-medium">
              {dashboardData.wordOfTheDay.difficulty === 'beginner' ? 'Beginner' :
               dashboardData.wordOfTheDay.difficulty === 'intermediate' ? 'Intermediate' : 'Advanced'}
            </span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <div className="text-4xl font-bold text-gray-900 dark:text-white mb-4 text-center">
                {dashboardData.wordOfTheDay.persian}
              </div>
              <div className="text-center mb-6">
                <p className="text-lg text-gray-600 dark:text-gray-300 mb-2">
                  {dashboardData.wordOfTheDay.transliteration}
                </p>
                <p className="text-xl font-semibold text-gray-900 dark:text-white">
                  {dashboardData.wordOfTheDay.english}
                </p>
              </div>
              <div className="text-center">
                <span className="inline-block px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg">
                  {dashboardData.wordOfTheDay.category}
                </span>
              </div>
            </div>
            
            <div className="flex flex-col justify-center space-y-4">
              <button 
                onClick={() => playSmartAudio(undefined, dashboardData.wordOfTheDay.persian).catch(console.error)}
                className="w-full py-3 px-6 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-red-600 transition-all duration-300"
              >
                🎵 Play Pronunciation
              </button>
              <button className="w-full py-3 px-6 border-2 border-orange-500 text-orange-500 dark:text-orange-400 font-semibold rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-all duration-300">
                📚 Add to Learning List
              </button>
              <Link
                to="/word-of-the-day"
                className="w-full py-3 px-6 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-300 text-center"
              >
                View Previous Words
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Recent Activity */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
          Recent Activity
        </h2>
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
          {dashboardData?.recentActivity.map((activity, index) => (
            <div
              key={activity.id}
              className={`p-6 ${index !== (dashboardData?.recentActivity.length || 0) - 1 ? 'border-b border-gray-200 dark:border-gray-700' : ''}`}
            >
              <div className="flex items-start space-x-4 rtl:space-x-reverse">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900 rounded-lg flex items-center justify-center">
                    <span className="text-lg">{activity.icon}</span>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {activity.title}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {activity.description}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    {activity.timestamp.toLocaleDateString('en-US')} - {activity.timestamp.toLocaleTimeString('en-US')}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Study Reminder */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl p-8 text-white">
        <div className="text-center">
          <h3 className="text-2xl font-bold mb-4">
            Regular learning is the key to success! 🎯
          </h3>
          <p className="text-blue-100 mb-6">
            Study just 15 minutes every day and watch your remarkable progress
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/flashcards"
              className="px-6 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-all duration-300"
            >
              Start Studying
            </Link>
            <Link
              to="/progress"
              className="px-6 py-3 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-blue-600 transition-all duration-300"
            >
              View Progress
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 